import React from 'react';
import { AlertTriangle } from 'lucide-react';
import SearchableDropdown from '../SearchableDropdown';
import { discountInputError, type DiscountInput } from '../../services/billingApi';

/**
 * ONE per-package discount editor, used by every screen that discounts a
 * package.
 *
 * There are two such screens — the tenant's Plan & pricing builder
 * (`PlanAssignmentBuilder`) and the Deals builder (`DealsPage`) — and they
 * write the SAME `DiscountSchema` shape, deliberately, so a deal converts into
 * a plan assignment by copy rather than by translation. A second, divergent
 * copy of this control is exactly how the two would drift: one rounding a
 * percentage differently, or one allowing a mode combination the schema
 * refuses.
 *
 * The percent-or-amount contract is structural, not validated: the input
 * carries ONE `mode`, so a body with both `percentOff` and `amountOffCents` is
 * not something this control can produce.
 */

const MODE_OPTIONS = [
  { value: 'none', label: 'No discount' },
  { value: 'percent', label: '% off' },
  { value: 'amount', label: '$ off' },
];

export interface DiscountFieldsProps {
  /** Label over the mode dropdown — "Discount", "Overall discount", … */
  label: string;
  value: DiscountInput;
  /** Receives a PARTIAL patch; the caller merges it into its own state. */
  onChange: (patch: Partial<DiscountInput>) => void;
  /** Wrapper classes, so a caller can add its own top margin. */
  rowClassName?: string;
  /** Width of the mode dropdown column. */
  modeWidthClass?: string;
  percentPlaceholder?: string;
  amountPlaceholder?: string;
  reasonPlaceholder?: string;
  /**
   * Why this discount was given. Kept on the record — and therefore ADMIN-ONLY:
   * it is free-text negotiation detail and must never reach a customer-facing
   * response. Hidden (not merely blank) where a screen has nowhere to put it.
   */
  showReason?: boolean;
  disabled?: boolean;
  /**
   * How the mode is chosen.
   *
   * `dropdown` (default) is the original select — `PlanAssignmentBuilder` keeps
   * it, so the tenant billing screen is untouched by this addition.
   *
   * `segmented` is a three-way radio — None / % / $ — with the number sitting
   * directly beside it, for screens that show one of these on every row and
   * where opening a dropdown per package is the slow part.
   *
   * Both write the SAME `DiscountInput`, so nothing about what is stored
   * depends on which control drew it.
   */
  variant?: 'dropdown' | 'segmented';
  /** Surface the segmented variant is drawn on. Ignored by `dropdown`. */
  tone?: 'light' | 'dark';
}

export const DiscountFields: React.FC<DiscountFieldsProps> = ({
  label, value, onChange,
  rowClassName = 'flex items-end gap-2 flex-wrap',
  modeWidthClass = 'w-36',
  percentPlaceholder = '10',
  amountPlaceholder = '25.00',
  reasonPlaceholder = 'e.g. multi-store commitment',
  showReason = true,
  disabled = false,
  variant = 'dropdown',
  tone = 'light',
}) => {
  const dark = tone === 'dark';
  const error = discountInputError(value);

  /** Switching mode CLEARS the typed number — the same rule the dropdown uses. */
  const pickMode = (mode: DiscountInput['mode']) =>
    onChange({ mode, value: mode === 'none' ? '' : value.value });

  return (
    <>
      <div className={rowClassName}>
        {variant === 'segmented' ? (
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase tracking-wider ${dark ? 'text-[#6b6b74]' : 'text-stone-500'}`}>{label}</span>
            <div
              role="radiogroup"
              aria-label={label}
              className={`inline-flex rounded-lg border overflow-hidden h-8 ${dark ? 'border-[rgba(255,255,255,.12)] bg-[#0c0c0f]' : 'border-stone-300 bg-white'}`}
            >
              {MODE_OPTIONS.map((opt, i) => {
                const active = value.mode === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={disabled}
                    onClick={() => pickMode(opt.value as DiscountInput['mode'])}
                    className={`px-2.5 text-[11px] font-semibold leading-none transition-colors disabled:opacity-50 ${
                      i > 0 ? (dark ? 'border-l border-[rgba(255,255,255,.12)]' : 'border-l border-stone-300') : ''
                    } ${
                      active
                        ? 'bg-rose-600 text-white'
                        : dark
                          ? 'bg-[#0c0c0f] text-[#6b6b74] hover:text-[#a1a1aa]'
                          : 'bg-white text-stone-600 hover:bg-stone-50'
                    }`}
                  >
                    {opt.value === 'none' ? 'None' : opt.value === 'percent' ? '%' : '$'}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
        <div className={modeWidthClass}>
          <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1">
            {label}
          </label>
          <SearchableDropdown
            options={MODE_OPTIONS}
            value={value.mode}
            // Switching to "No discount" CLEARS the typed number rather than
            // parking it out of sight, so a mode toggle can never resurrect an
            // amount the operator thought they had removed.
            onChange={(v) => onChange({ mode: (v as any) || 'none', value: v === 'none' ? '' : value.value })}
            variant="light"
            disabled={disabled}
          />
        </div>
        )}
        {value.mode !== 'none' && (
          <>
            {variant === 'segmented' ? (
              <div className="relative w-24">
                <input
                  type="number"
                  min={0}
                  max={value.mode === 'percent' ? 100 : undefined}
                  step={value.mode === 'percent' ? 1 : 0.01}
                  value={value.value}
                  disabled={disabled}
                  autoFocus
                  onChange={(e) => onChange({ value: e.target.value })}
                  placeholder={value.mode === 'percent' ? percentPlaceholder : amountPlaceholder}
                  className={`w-full h-8 pl-2.5 pr-6 rounded-lg text-sm tabular-nums border ${
                    dark
                      ? 'bg-[#0c0c0f] border-[rgba(255,255,255,.12)] text-stone-100 placeholder-[#52525b]'
                      : 'bg-white border-stone-300 text-stone-800 placeholder-stone-400'
                  }`}
                />
                <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[11px] pointer-events-none ${dark ? 'text-[#52525b]' : 'text-stone-400'}`}>
                  {value.mode === 'percent' ? '%' : '$'}
                </span>
              </div>
            ) : (
            <div className="w-28">
              <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1">
                {value.mode === 'percent' ? 'Percent' : 'Amount ($)'}
              </label>
              <input
                type="number"
                min={0}
                max={value.mode === 'percent' ? 100 : undefined}
                step={value.mode === 'percent' ? 1 : 0.01}
                value={value.value}
                disabled={disabled}
                onChange={(e) => onChange({ value: e.target.value })}
                placeholder={value.mode === 'percent' ? percentPlaceholder : amountPlaceholder}
                className="w-full px-3 py-2.5 bg-white border border-stone-200 rounded-xl text-stone-800 placeholder-stone-400 text-sm min-h-[44px]"
              />
            </div>
            )}
            {showReason && (
              variant === 'segmented' ? (
                <input
                  type="text"
                  value={value.reason || ''}
                  disabled={disabled}
                  onChange={(e) => onChange({ reason: e.target.value })}
                  placeholder="why (on the record)"
                  className={`flex-1 min-w-[9rem] h-8 px-2.5 rounded-lg text-xs border ${
                    dark
                      ? 'bg-[#0c0c0f] border-[rgba(255,255,255,.12)] text-stone-100 placeholder-[#52525b]'
                      : 'bg-white border-stone-300 text-stone-800 placeholder-stone-400'
                  }`}
                />
              ) : (
              <div className="flex-1 min-w-[10rem]">
                <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1">
                  Why (kept on the record)
                </label>
                <input
                  type="text"
                  value={value.reason || ''}
                  disabled={disabled}
                  onChange={(e) => onChange({ reason: e.target.value })}
                  placeholder={reasonPlaceholder}
                  className="w-full px-3 py-2.5 bg-white border border-stone-200 rounded-xl text-stone-800 placeholder-stone-400 text-sm min-h-[44px]"
                />
              </div>
              )
            )}
          </>
        )}
      </div>
      {error && (
        <p className="mt-2 text-xs text-red-700 flex items-center gap-1.5">
          <AlertTriangle size={13} /> {error}
        </p>
      )}
    </>
  );
};

export default DiscountFields;
